import React from 'react';
import { GridCellProps, GridItemChangeEvent } from '@progress/kendo-react-grid';
import { RadioButtonCell } from '../../grid/customCells/RadioButtonCell';
import { RemoveCell } from '../../grid/customCells/RemoveCell';
import { CpsGridCell } from '../../grid/customCells/CpsGridCell';
import { EditableDropdownCell } from '../../grid/customCells/EditableDropdownCell';
import { CpsField, DEFAULT_FACETS_COUNT, EDITING_VIEW_FIELD, PERMISSIONS_FIELD } from '@cs-cms/types';

export const useCellRenderers = (
  editingViewState: any,
  setEditingViewState: any,
  setHasUnsavedChanges: any,
  allFields?: CpsField[],
  extensionFieldDisplayName?: string
) => {
  const updateFieldsState = (updates: any[]) => {
    if (!setEditingViewState) {
      return;
    }

    setEditingViewState({ ...editingViewState, fields: updates });
    setHasUnsavedChanges?.(true);
  };

  const getAvailableFieldsForRow = (currentFieldId: string | number) => {
    if (!allFields) {
      return [];
    }

    const usedFieldIds = editingViewState.fields
      .filter((field: any) => field.id !== currentFieldId)
      .map((field: any) => field.id);
    return allFields
      .filter(field => !field.technical && !usedFieldIds.includes(field.id))
      .map(field => ({ id: field.id, name: field.displayName || field.name }));
  };

  const renderLinkCell = (cellProps: GridCellProps) => {
    const dataItem = cellProps.dataItem;
    return (
      <RadioButtonCell
        dataItem={cellProps.dataItem}
        field={cellProps.field}
        dataIndex={cellProps.dataIndex}
        id={cellProps.id}
        columnPosition={cellProps.columnPosition}
        onChange={cellProps.onChange}
        className={cellProps.className}
        colSpan={cellProps.colSpan}
        ariaColumnIndex={cellProps.ariaColumnIndex}
        isSelected={cellProps.isSelected}
        style={cellProps.style}
        rowType={cellProps.rowType}
        level={cellProps.level}
        expanded={cellProps.expanded}
        disabled={dataItem.name === extensionFieldDisplayName}
        onRadioButtonChange={() => {
          const updates = editingViewState.fields.map((f: any) => ({
            ...f,
            isDocumentLink: f.id === dataItem.id ? !f.isDocumentLink : false
          }));
          updateFieldsState(updates);
        }}
      />
    );
  };

  const renderRemoveCell = (cellProps: GridCellProps) => {
    const dataItem = cellProps.dataItem;
    return (
      <RemoveCell
        dataItem={cellProps.dataItem}
        field={cellProps.field}
        dataIndex={cellProps.dataIndex}
        id={cellProps.id}
        columnPosition={cellProps.columnPosition}
        onChange={cellProps.onChange}
        className={cellProps.className}
        colSpan={cellProps.colSpan}
        ariaColumnIndex={cellProps.ariaColumnIndex}
        isSelected={cellProps.isSelected}
        style={cellProps.style}
        rowType={cellProps.rowType}
        level={cellProps.level}
        expanded={cellProps.expanded}
        onClick={() => {
          const filteredFields = editingViewState.fields.filter((f: any) => f.id !== dataItem.id);
          updateFieldsState(filteredFields);
        }}
      />
    );
  };

  const renderNameCell = (cellProps: GridCellProps) => {
    const dataItem = cellProps.dataItem;
    const isFieldIncomplete = !dataItem.id || dataItem.id < 0 || !dataItem.name;
    const availableFieldsForRow = getAvailableFieldsForRow(dataItem.id);

    return (
      <EditableDropdownCell
        dataItem={cellProps.dataItem}
        field={cellProps.field}
        dataIndex={cellProps.dataIndex}
        id={cellProps.id}
        columnPosition={cellProps.columnPosition}
        onChange={cellProps.onChange}
        className={cellProps.className}
        colSpan={cellProps.colSpan}
        ariaColumnIndex={cellProps.ariaColumnIndex}
        isSelected={cellProps.isSelected}
        rowType={cellProps.rowType}
        level={cellProps.level}
        expanded={cellProps.expanded}
        onItemChange={(event: GridItemChangeEvent) => {
          const selectedField = allFields?.find((f: any) => f.id === event.value.id);
          const isFacetableOverride = selectedField?.name === PERMISSIONS_FIELD;
          const isFacetable = isFacetableOverride ? true : (selectedField?.facetable ?? false);
          const updates = editingViewState.fields.map((f: any) =>
            f.id === dataItem.id || !f.id
              ? {
                  ...f,
                  id: event.value.id,
                  name: event.value.name,
                  facetable: isFacetable,
                  facetsCount: isFacetable ? DEFAULT_FACETS_COUNT : undefined,
                  isNew: undefined
                }
              : f
          );
          updateFieldsState(updates);
        }}
        options={availableFieldsForRow}
        dataItemKey="id"
        textField={EDITING_VIEW_FIELD.NAME}
        size="medium"
        style={{
          border: isFieldIncomplete ? '1px solid red' : undefined,
          borderRadius: isFieldIncomplete ? '4px' : undefined
        }}
      />
    );
  };

  const renderDefaultCell = (cellProps: GridCellProps) => {
    const dataItem = cellProps.dataItem;
    const fieldValue = cellProps.field ? dataItem[cellProps.field] : '';
    return (
      <CpsGridCell
        dataItem={cellProps.dataItem}
        field={cellProps.field}
        dataIndex={cellProps.dataIndex}
        id={cellProps.id}
        columnPosition={cellProps.columnPosition}
        onChange={cellProps.onChange}
        className={cellProps.className}
        colSpan={cellProps.colSpan}
        ariaColumnIndex={cellProps.ariaColumnIndex}
        isSelected={cellProps.isSelected}
        style={cellProps.style}
        rowType={cellProps.rowType}
        level={cellProps.level}
        expanded={cellProps.expanded}
        title={fieldValue || ''}
      >
        {fieldValue || ''}
      </CpsGridCell>
    );
  };

  return {
    renderLinkCell,
    renderRemoveCell,
    renderNameCell,
    renderDefaultCell
  };
};
